/**
 * The panels that belong INSIDE a page, not beside it.
 *
 * Candidate response times, sent emails, draft ratings and interview outcomes used to be four
 * top-level sections stacked above the usage figures — in the order they happened to be built, so
 * the board opened on "candidate requests" and the question it is actually opened with ("which
 * pages are used?") was five screens down. Each of them measures one page, so each now lives
 * inside that page, collapsed, under the tab it belongs to.
 *
 * They keep their own queries: these read real records (triage rows, bookings, stored votes)
 * rather than the analytics event stream, so they still answer when the event pipeline is empty —
 * which is exactly when somebody is most likely to be looking.
 */
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@finn-auto/design-system/atoms/accordion';
import {
  fetchBoardUsage,
  fetchDraftFeedbackStats,
  fetchInterviewOutcomes,
  fetchResponseStats,
  fetchSentEmailStats,
} from '../../services/usageApi';
import { ResponseSection } from './ResponseSection';
import { SentEmailSection } from './SentEmailSection';
import { OutcomesSection } from './OutcomesSection';
import { DraftFeedbackSection } from './DraftFeedbackSection';
import { BoardUsageSection } from './BoardUsageSection';
import { FeatureAdoption } from './FeatureAdoption';

/** Pages that have a deep dive, so the tree can show them even in a range with no events. */
export const PAGES_WITH_DETAIL = ['ta-action-boards', 'interviews'];

function Fold({
  value,
  title,
  hint,
  children,
}: {
  value: string;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <AccordionItem value={value} className="overflow-hidden rounded-lg border border-zinc-100">
      <AccordionTrigger className="px-3 py-2 hover:no-underline">
        <span className="flex flex-1 flex-col gap-0.5 pr-3 text-left">
          <span className="text-xs font-semibold text-zinc-900">{title}</span>
          <span className="text-xs font-normal text-zinc-500">{hint}</span>
        </span>
      </AccordionTrigger>
      <AccordionContent
        alwaysMounted={false}
        className="border-t border-solid border-zinc-100 px-3 pt-3 pb-3"
      >
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

/** One shape for every "still loading" and "could not load" state in here. */
function Pending({ what }: { what: string }) {
  return <p className="py-6 text-center text-sm text-zinc-400">Loading {what}…</p>;
}

function Failed({ what }: { what: string }) {
  return <p className="py-4 text-sm text-zinc-500">Could not load {what}.</p>;
}

export function TaBoardDetail({ days }: { days: number }) {
  const boardUsage = useQuery({
    queryKey: ['usage-boards', days],
    queryFn: () => fetchBoardUsage(days),
  });
  const response = useQuery({
    queryKey: ['ta-response-stats', days],
    queryFn: () => fetchResponseStats(days),
  });
  const sentEmail = useQuery({
    queryKey: ['sent-email-stats', days],
    queryFn: () => fetchSentEmailStats(days),
  });
  const draftFeedback = useQuery({
    queryKey: ['draft-feedback-stats', days],
    queryFn: () => fetchDraftFeedbackStats(days),
  });

  return (
    <div className="space-y-4">
      {/* Open by default, and the only thing here that is: it is the reason somebody expands this
          page, and a coaching list behind a second click is a coaching list nobody reads. */}
      {boardUsage.isPending ? (
        <Pending what="board usage" />
      ) : boardUsage.data ? (
        <>
          {/* Adoption first, boards second: "has the team picked this up" is the question the
              board is opened with, and "who has not" is how you act on the answer. */}
          <FeatureAdoption recruiters={boardUsage.data.recruiters} />
          <div className="border-t border-zinc-100 pt-4">
            <BoardUsageSection usage={boardUsage.data} />
          </div>
        </>
      ) : (
        <Failed what="the per-board usage" />
      )}

      <Accordion type="multiple" className="space-y-1.5">
        <Fold
          value="requests"
          title="Candidate requests — volume and response times"
          hint="From triage records, not analytics events."
        >
          {response.isPending ? (
            <Pending what="response statistics" />
          ) : response.data ? (
            <ResponseSection stats={response.data} />
          ) : (
            <Failed what="the response statistics" />
          )}
        </Fold>

        <Fold
          value="draft-ratings"
          title="Draft quality, rated by recruiters"
          hint="Thumbs on the replies the Requests tab drafts, worst-first by question type and source."
        >
          {draftFeedback.isPending ? (
            <Pending what="draft ratings" />
          ) : draftFeedback.data ? (
            <DraftFeedbackSection stats={draftFeedback.data} />
          ) : (
            <Failed what="the draft ratings" />
          )}
        </Fold>

        <Fold
          value="sent-email"
          title="Emails to candidates"
          hint="What the board sent and when, from the send records — counts only, never a recipient."
        >
          {sentEmail.isPending ? (
            <Pending what="sent-email statistics" />
          ) : sentEmail.data ? (
            <SentEmailSection stats={sentEmail.data} />
          ) : (
            <Failed what="the sent-email statistics" />
          )}
        </Fold>
      </Accordion>
    </div>
  );
}

export function LogDetail({ days }: { days: number }) {
  const outcomes = useQuery({
    queryKey: ['interview-outcomes', days],
    queryFn: () => fetchInterviewOutcomes(days),
  });

  return (
    <Accordion type="multiple" className="space-y-1.5">
      <Fold
        value="outcomes"
        title="Interview outcomes"
        hint="What happened to the interviews that were booked — and, where candidates said so, why. Read from the booking records themselves."
      >
        {outcomes.isPending ? (
          <Pending what="interview outcomes" />
        ) : outcomes.data ? (
          <OutcomesSection outcomes={outcomes.data} />
        ) : (
          <Failed what="the interview outcomes" />
        )}
      </Fold>
    </Accordion>
  );
}
